import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetBeacon4PerformanceDashboard, GetBeacon4PerformanceTable, GetBeacon4PerformanceAvailableKpis } from '../requests/Beacon4PerformanceRequests';

export const loadBeacon4PerformanceDashboard = createAsyncThunk(
	'beacon4PerformanceKpiData/loadDashboard',
	async (params, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceDashboard(params);
			return {
				tech: params.tech,
				mainKpiKey: params.mainKpiKey,
				data: res?.data ?? res,
			};
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Lookup of available techs + main KPIs per tech (drives the FilterBar dropdowns)
export const loadBeacon4PerformanceAvailableKpis = createAsyncThunk(
	'beacon4PerformanceKpiData/loadAvailableKpis',
	async (_, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceAvailableKpis();
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Aggregation Mode table (left panel KPI broken down by spatial dimension over time)
export const loadBeacon4PerformanceTable = createAsyncThunk(
	'beacon4PerformanceKpiData/loadTable',
	async (params, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceTable(params);
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

const beacon4PerformanceKpiDataSlice = createSlice({
	name: 'beacon4PerformanceKpiData',
	initialState: {
		// { [tech]: { [mainKpiKey]: { leftPanel, rightPanel } } }
		dashboardByTech: {},
		table:        null,    // last-loaded aggregation table
		tableLoading: false,
		availableTechs: [],
		availableKpisByTech: {},
		availableLoading: false,
		loading:      false,
		error:        null,
	},
	reducers: {
		clearBeacon4PerformanceDashboard(state) {
			state.dashboardByTech = {};
			state.table   = null;
			state.loading = false;
			state.error   = null;
		},
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4PerformanceDashboard.pending, (s) => { s.loading = true; s.error = null; })
		 .addCase(loadBeacon4PerformanceDashboard.fulfilled, (s, a) => {
			s.loading = false;
			const { tech, mainKpiKey, data } = a.payload;
			if (!s.dashboardByTech[tech]) s.dashboardByTech[tech] = {};
			s.dashboardByTech[tech][mainKpiKey] = data;
		 })
		 .addCase(loadBeacon4PerformanceDashboard.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

		 .addCase(loadBeacon4PerformanceTable.pending,   (s) => { s.tableLoading = true; })
		 .addCase(loadBeacon4PerformanceTable.fulfilled, (s, a) => { s.tableLoading = false; s.table = a.payload; })
		 .addCase(loadBeacon4PerformanceTable.rejected,  (s, a) => { s.tableLoading = false; s.error = a.payload; })

		 .addCase(loadBeacon4PerformanceAvailableKpis.pending,   (s) => { s.availableLoading = true; })
		 .addCase(loadBeacon4PerformanceAvailableKpis.fulfilled, (s, a) => {
			s.availableLoading = false;
			s.availableTechs       = a.payload?.techs ?? [];
			s.availableKpisByTech  = a.payload?.kpisByTech ?? {};
		 })
		 .addCase(loadBeacon4PerformanceAvailableKpis.rejected,  (s, a) => { s.availableLoading = false; s.error = a.payload; });
	},
});

export const { clearBeacon4PerformanceDashboard } = beacon4PerformanceKpiDataSlice.actions;
export default beacon4PerformanceKpiDataSlice.reducer;

export const selectBeacon4PerformanceDashboardByTech = (state) => state.beacon4PerformanceKpiData.dashboardByTech;
export const selectBeacon4PerformanceLoading         = (state) => state.beacon4PerformanceKpiData.loading;
export const selectBeacon4PerformanceTable           = (state) => state.beacon4PerformanceKpiData.table;
export const selectBeacon4PerformanceTableLoading    = (state) => state.beacon4PerformanceKpiData.tableLoading;
export const selectBeacon4PerformanceAvailableTechs      = (state) => state.beacon4PerformanceKpiData.availableTechs;
export const selectBeacon4PerformanceAvailableKpisByTech = (state) => state.beacon4PerformanceKpiData.availableKpisByTech;
export const selectBeacon4PerformanceAvailableLoading    = (state) => state.beacon4PerformanceKpiData.availableLoading;
